import React, { useState, useEffect } from 'react';
import {
    Typography,
    Fade,
    Zoom,
    TextField,
    Button,
    IconButton,
    Divider,
    MenuItem,
    InputAdornment,
    Alert,
    CircularProgress,
    Box,
    Avatar
} from '@mui/material';
import {
    Edit,
    Save,
    Cancel,
    Person,
    Email,
    Phone,
    LocationOn,
    CalendarToday,
    Badge,
    CloudUpload,
    Delete
} from '@mui/icons-material';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-toastify';
import UserService from '../../services/UserService';
import AuthService from '../../services/AuthService';

// Importation des composants réutilisables
import PhoneNumberInput from '../../utils/PhoneNumberInput';
import CountryInput from '../../utils/CountryInput';
import CityInput from '../../utils/CityInput';
import NeighborhoodInput from '../../utils/NeighborhoodInput';

// Configuration Supabase
const supabaseUrl = 'https://yrbhgwbzukfejvcphnwa.supabase.co';
const supabaseKey = 'sb_publishable_6drosvFnB3dDfyXC4X9XLg_cbhqx2S-';
const supabase = createClient(supabaseUrl, supabaseKey);

const InformationPersonnelle = () => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        neighborhood: "",
        city: "",
        country: "",
        username: "",
        additionalInfos: "",
        profilePictureUrl: "",
        dateOfBirth: "",
        gender: "",
        useRef: ""
    });

    // États pour l'upload d'image
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Fonction pour générer le nom de fichier selon le format demandé
    const generateFileName = (file) => {
        const currentUser = AuthService.getCurrentUser();
        const username = currentUser?.username || 'user';
        const fileExt = file.name.split('.').pop().toLowerCase();
        const now = new Date();
        const dateStr = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
        const randomNum = Math.floor(10000 + Math.random() * 90000);

        return `${username}-${dateStr}-${randomNum}.${fileExt}`;
    };

    // Fonction pour uploader l'image vers Supabase
    const uploadImageToSupabase = async (file) => {
        try {
            setIsUploading(true);
            toast.info('Upload de la photo de profil en cours...');

            const fileName = generateFileName(file);
            const filePath = `profile/${fileName}`;

            const { data, error } = await supabase.storage
                .from('driven-home-services')
                .upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (error) {
                console.error('Erreur lors de l\'upload:', error);
                toast.error(`Erreur lors de l'upload: ${error.message}`);
                throw new Error(`Erreur upload: ${error.message}`);
            }

            const publicUrl = `${supabaseUrl}/storage/v1/object/public/driven-home-services/${filePath}`;

            console.log('Photo uploadée avec succès:', {
                fileName: fileName,
                filePath: filePath,
                publicUrl: publicUrl
            });

            toast.success('Photo de profil uploadée avec succès!');
            return publicUrl;

        } catch (error) {
            console.error('Erreur lors de l\'upload de l\'image:', error);
            toast.error('Erreur lors de l\'upload de la photo de profil');
            throw error;
        } finally {
            setIsUploading(false);
        }
    };

    // Gestion de la sélection d'image
    const handleImageSelect = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error(`Type de fichier non supporté: ${file.name}. Utilisez JPG, PNG, GIF ou WebP.`);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error(`L'image ${file.name} est trop volumineuse (max 5MB)`);
            return;
        }

        setSelectedImage(file);
        setImageError(false);

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

        toast.info('Nouvelle photo sélectionnée. Cliquez sur Enregistrer pour sauvegarder.');
    };

    // Supprimer l'image sélectionnée
    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview('');
        setImageError(false);
        setFormData(prev => ({ ...prev, profilePictureUrl: '' }));
        toast.info('Photo supprimée');
    };

    // Fonction pour formater la date depuis le backend vers le format input date
    const formatDateFromBackend = (dateString) => {
        if (!dateString) return "";

        try {
            let dateParts;

            if (dateString.includes('-')) {
                if (dateString.includes(' ')) {
                    const [datePart] = dateString.split(' ');
                    dateParts = datePart.split('-');

                    if (dateParts.length === 3) {
                        const [day, month, year] = dateParts;
                        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                    }
                } else {
                    return dateString;
                }
            }

            return "";
        } catch (error) {
            console.error('Erreur de formatage de date:', error);
            return "";
        }
    };

    // Fonction pour formater la date vers le backend
    const formatDateToBackend = (dateString) => {
        if (!dateString) return "";

        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year} 00:00`;
        } catch (error) {
            console.error('Erreur de formatage de date:', error);
            return "";
        }
    };

    // Fonction pour afficher la date en format lisible
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return "Non spécifié";

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Date invalide";

            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return "Date invalide";
        }
    };

    // Récupérer les données utilisateur au chargement
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true);
                setError(null);

                const currentUser = AuthService.getCurrentUser();

                if (!currentUser || !currentUser.id) {
                    throw new Error('Utilisateur non connecté');
                }

                const userResponse = await UserService.getUserById(currentUser.id);

                console.log('Données utilisateur reçues:', userResponse);

                const formattedDateOfBirth = formatDateFromBackend(userResponse.dateOfBirth);

                setFormData({
                    firstName: userResponse.firstName || "",
                    lastName: userResponse.lastName || "",
                    email: userResponse.email || "",
                    phoneNumber: userResponse.phoneNumber || "",
                    neighborhood: userResponse.neighborhood || "",
                    city: userResponse.city || "",
                    country: userResponse.country || "",
                    username: userResponse.username || "",
                    additionalInfos: userResponse.additionalInfos || "",
                    profilePictureUrl: userResponse.profilePictureUrl || "",
                    dateOfBirth: formattedDateOfBirth,
                    gender: userResponse.gender || "",
                    useRef: userResponse.useRef || ""
                });

                if (userResponse.profilePictureUrl) {
                    setImagePreview(userResponse.profilePictureUrl);
                }

            } catch (error) {
                console.error('Erreur lors de la récupération des données:', error);
                setError('Impossible de charger les données utilisateur');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handlers spécifiques pour les composants réutilisables
    const handlePhoneNumberChange = (value) => {
        setFormData(prev => ({ ...prev, phoneNumber: value }));
    };

    const handleCountryChange = (value) => {
        setFormData(prev => ({ ...prev, country: value }));
    };

    const handleCityChange = (value) => {
        setFormData(prev => ({ ...prev, city: value }));
    };

    const handleNeighborhoodChange = (value) => {
        setFormData(prev => ({ ...prev, neighborhood: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const currentUser = AuthService.getCurrentUser();

            if (!currentUser || !currentUser.id) {
                throw new Error('Utilisateur non connecté');
            }

            let profilePictureUrl = formData.profilePictureUrl;

            if (selectedImage) {
                console.log('Début de l\'upload de la photo de profil...');
                profilePictureUrl = await uploadImageToSupabase(selectedImage);
                console.log('Upload terminé, URL:', profilePictureUrl);
            }

            const formattedDateOfBirth = formatDateToBackend(formData.dateOfBirth);

            const updateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                neighborhood: formData.neighborhood,
                city: formData.city,
                country: formData.country,
                additionalInfos: formData.additionalInfos,
                profilePictureUrl: profilePictureUrl,
                dateOfBirth: formattedDateOfBirth,
                gender: formData.gender,
                lastModifiedBy: currentUser.username
            };

            console.log('Données envoyées au backend:', updateData);

            await UserService.updateUser(currentUser.id, updateData);

            setSuccess('Modifications sauvegardées avec succès');
            setIsEditMode(false);

            if (profilePictureUrl) {
                setImagePreview(profilePictureUrl);
            }

        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            setError('Erreur lors de la sauvegarde des modifications');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        const fetchUserData = async () => {
            try {
                const currentUser = AuthService.getCurrentUser();
                if (currentUser && currentUser.id) {
                    const userResponse = await UserService.getUserById(currentUser.id);

                    const formattedDateOfBirth = formatDateFromBackend(userResponse.dateOfBirth);

                    setFormData({
                        firstName: userResponse.firstName || "",
                        lastName: userResponse.lastName || "",
                        email: userResponse.email || "",
                        phoneNumber: userResponse.phoneNumber || "",
                        neighborhood: userResponse.neighborhood || "",
                        city: userResponse.city || "",
                        country: userResponse.country || "",
                        username: userResponse.username || "",
                        additionalInfos: userResponse.additionalInfos || "",
                        profilePictureUrl: userResponse.profilePictureUrl || "",
                        dateOfBirth: formattedDateOfBirth,
                        gender: userResponse.gender || "",
                        useRef: userResponse.useRef || ""
                    });

                    if (userResponse.profilePictureUrl) {
                        setImagePreview(userResponse.profilePictureUrl);
                    } else {
                        setImagePreview('');
                    }
                }
            } catch (error) {
                console.error('Erreur lors du rechargement des données:', error);
            }
        };

        fetchUserData();
        setSelectedImage(null);
    };

    // Fonction pour afficher le genre en format lisible
    const getGenderDisplay = (gender) => {
        switch (gender) {
            case 'MALE': return 'Homme';
            case 'FEMALE': return 'Femme';
            case 'OTHER': return 'Autre';
            default: return 'Non spécifié';
        }
    };

    // Composant pour afficher un champ en mode lecture
    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié" }) => (
        <Box className="display-field">
            <Typography variant="body2" color="textSecondary" className="field-label">
                {icon} {label}
            </Typography>
            <Typography variant="body1" className="field-value">
                {value || emptyText}
            </Typography>
        </Box>
    );

    if (loading) {
        return (
            <div className="profile-main-content">
                <div className="profile-card">
                    <div className="profile-card-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Chargement des données...
                        </Typography>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-main-content">
            <Fade in timeout={1000}>
                <div className="profile-card">
                    <div className="profile-card-content">
                        {/* Messages d'alerte */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        <div className="profile-header">
                            <Typography variant="h4" className="profile-title">
                                Information Personnelle
                            </Typography>

                            {!isEditMode ? (
                                <IconButton
                                    className="profile-edit-btn"
                                    onClick={() => setIsEditMode(true)}
                                >
                                    <Edit />
                                    Éditer
                                </IconButton>
                            ) : (
                                <Zoom in={isEditMode}>
                                    <div className="profile-actions">
                                        <Button
                                            variant="outlined"
                                            startIcon={<Cancel />}
                                            onClick={handleCancel}
                                            className="profile-cancel-btn"
                                            disabled={saving || isUploading}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={saving || isUploading ? <CircularProgress size={20} /> : <Save />}
                                            onClick={handleSave}
                                            className="profile-save-btn"
                                            disabled={saving || isUploading}
                                        >
                                            {saving || isUploading ? 'Sauvegarde...' : 'Enregistrer'}
                                        </Button>
                                    </div>
                                </Zoom>
                            )}
                        </div>

                        {/* Section Photo de Profil */}
                        <Box className="profile-picture-section" sx={{ mb: 4 }}>
                            <Typography variant="h6" className="form-section-title" sx={{ mb: 2 }}>
                                Photo de Profil
                            </Typography>

                            <Box className="profile-picture-container" sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box className="avatar-container" sx={{ position: 'relative' }}>
                                    <Avatar
                                        src={imagePreview || "/default-avatar.png"}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            border: '3px solid',
                                            borderColor: 'primary.main'
                                        }}
                                        className="profile-avatar"
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2, width: '100%' }} />

                        {/* Formulaire avec autoComplete="off" */}
                        <form autoComplete="off">
                            <div className="profile-form-grid">
                                {/* Informations de base */}
                                <Typography variant="h6" className="form-section-title">
                                    Informations de base
                                </Typography>

                                {isEditMode ? (
                                    <>
                                        <Fade in timeout={1200}>
                                            <TextField
                                                fullWidth
                                                label="Prénom"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Person sx={{ color: 'primary.main' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Fade>

                                        <Fade in timeout={1300}>
                                            <TextField
                                                fullWidth
                                                label="Nom"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Person sx={{ color: 'primary.main' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Fade>

                                        <Fade in timeout={1400}>
                                            <TextField
                                                fullWidth
                                                label="Nom d'utilisateur"
                                                name="username"
                                                value={formData.username}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                                InputProps={{
                                                    readOnly: true,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Badge sx={{ color: 'text.secondary' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                helperText="Le nom d'utilisateur ne peut pas être modifié"
                                            />
                                        </Fade>

                                        <Fade in timeout={1500}>
                                            <TextField
                                                fullWidth
                                                label="Email"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Email sx={{ color: 'primary.main' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Fade>
                                    </>
                                ) : (
                                    <>
                                        <DisplayField
                                            label="Prénom"
                                            value={formData.firstName}
                                            icon={<Person sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Nom"
                                            value={formData.lastName}
                                            icon={<Person sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Nom d'utilisateur"
                                            value={formData.username}
                                            icon={<Badge sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Email"
                                            value={formData.email}
                                            icon={<Email sx={{ fontSize: 16 }} />}
                                        />
                                    </>
                                )}

                                <Divider sx={{ my: 2, width: '100%' }} />

                                {/* Informations de contact */}
                                <Typography variant="h6" className="form-section-title">
                                    Informations de contact
                                </Typography>

                                {isEditMode ? (
                                    <>
                                        <Fade in timeout={1600}>
                                            {/* Envelopper dans un Box permet à Fade d'avoir un élément DOM sur lequel s'accrocher */}
                                            <Box>
                                                <PhoneNumberInput
                                                    value={formData.phoneNumber}
                                                    onChange={handlePhoneNumberChange}
                                                    disabled={saving || isUploading}
                                                    defaultCountry="CM"
                                                />
                                            </Box>
                                        </Fade>

                                        <Fade in timeout={1700}>
                                            <Box>
                                                <CountryInput
                                                    value={formData.country}
                                                    onChange={handleCountryChange}
                                                    disabled={saving || isUploading}
                                                    required={true}
                                                />
                                            </Box>
                                        </Fade>

                                        <Fade in timeout={1800}>
                                            <Box>
                                                <CityInput
                                                    value={formData.city}
                                                    onChange={handleCityChange}
                                                    disabled={saving || isUploading}
                                                    required={true}
                                                />
                                            </Box>
                                        </Fade>

                                        <Fade in timeout={1900}>
                                            <Box>
                                                <NeighborhoodInput
                                                    value={formData.neighborhood}
                                                    onChange={handleNeighborhoodChange}
                                                    disabled={saving || isUploading}
                                                    required={true}
                                                />
                                            </Box>
                                        </Fade>
                                    </>
                                ) : (
                                    <>
                                        <DisplayField
                                            label="Téléphone"
                                            value={formData.phoneNumber}
                                            icon={<Phone sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Pays"
                                            value={formData.country}
                                            icon={<LocationOn sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Ville"
                                            value={formData.city}
                                            icon={<LocationOn sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Quartier"
                                            value={formData.neighborhood}
                                            icon={<LocationOn sx={{ fontSize: 16 }} />}
                                        />
                                    </>
                                )}

                                <Divider sx={{ my: 2, width: '100%' }} />

                                {/* Informations supplémentaires */}
                                <Typography variant="h6" className="form-section-title">
                                    Informations supplémentaires
                                </Typography>

                                {isEditMode ? (
                                    <>
                                        <Fade in timeout={2000}>
                                            <TextField
                                                fullWidth
                                                label="Date de naissance"
                                                name="dateOfBirth"
                                                type="date"
                                                value={formData.dateOfBirth}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CalendarToday sx={{ color: 'primary.main' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Fade>

                                        <Fade in timeout={2100}>
                                            <TextField
                                                fullWidth
                                                select
                                                label="Genre"
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                            >
                                                <MenuItem value="MALE">Homme</MenuItem>
                                                <MenuItem value="FEMALE">Femme</MenuItem>
                                            </TextField>
                                        </Fade>

                                        <Fade in timeout={2200}>
                                            <TextField
                                                fullWidth
                                                label="Référence"
                                                name="useRef"
                                                value={formData.useRef}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                                helperText="La référence ne peut pas être modifiée"
                                            />
                                        </Fade>

                                        <Fade in timeout={2300}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Informations supplémentaires"
                                                name="additionalInfos"
                                                value={formData.additionalInfos}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                                className="profile-text-field"
                                                autoComplete="off"
                                            />
                                        </Fade>
                                    </>
                                ) : (
                                    <>
                                        <DisplayField
                                            label="Date de naissance"
                                            value={formatDateForDisplay(formData.dateOfBirth)}
                                            icon={<CalendarToday sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Genre"
                                            value={getGenderDisplay(formData.gender)}
                                            icon={<Person sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Référence"
                                            value={formData.useRef}
                                            icon={<Badge sx={{ fontSize: 16 }} />}
                                        />
                                        <DisplayField
                                            label="Informations supplémentaires"
                                            value={formData.additionalInfos}
                                            icon={<Person sx={{ fontSize: 16 }} />}
                                        />
                                    </>
                                )}
                            </div>
                        </form>

                        {/* Statistiques */}
                        <div className="profile-stats">
                            <Fade in timeout={2400}>
                                <div className="profile-stat-item">
                                    <Typography variant="h6" className="profile-stat-value" style={{ color: '#4caf50' }}>
                                        {formData.firstName && formData.lastName && formData.email ? 'Complet' : 'Incomplet'}
                                    </Typography>
                                    <Typography variant="body2" className="profile-stat-label">
                                        Profil
                                    </Typography>
                                </div>
                            </Fade>

                            <Fade in timeout={2600}>
                                <div className="profile-stat-item">
                                    <Typography variant="h6" className="profile-stat-value" style={{ color: '#2196f3' }}>
                                        {new Date().toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body2" className="profile-stat-label">
                                        Dernière modification
                                    </Typography>
                                </div>
                            </Fade>
                        </div>
                    </div>
                </div>
            </Fade>
        </div>
    );
};

export default InformationPersonnelle;